import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BookOpen, ChevronDown, Search, X } from 'lucide-react'
import { EmptyState } from '@/components/EmptyState'
import { ManualArticle } from '@/components/ManualArticle'
import { Input } from '@/components/ui/input'
import { filterSections, isTableOfContents, parseManual } from '@/lib/manual'
import { SectionNavigationProvider, scrollToSection } from '@/lib/manual-navigation'
import manualSource from '../../../docs/manuale-utente.md?raw'

/**
 * The manual is a build-time asset: parsing it once at module scope keeps the
 * page free of both a network round-trip and a re-parse on every keystroke.
 * Its own table of contents is dropped — the side index already is one.
 */
const SECTIONS = parseManual(manualSource).filter((section) => !isTableOfContents(section))

/** Nested indentation of the index, by heading depth. */
const INDENT_BY_LEVEL: Record<number, string> = { 2: 'pl-2', 3: 'pl-6', 4: 'pl-10' }

/** The section a `#…` URL points at, empty when the URL carries no anchor. */
function anchoredSectionId(): string {
  return decodeURIComponent(window.location.hash.slice(1))
}

/**
 * Authenticated, in-app rendering of the user manual (`docs/manuale-utente.md`):
 * side index, client-side full-text search, Markdown content. The Markdown file
 * remains the single source of truth — nothing is duplicated here.
 */
export function Docs() {
  const { t } = useTranslation('common')
  const [query, setQuery] = useState('')
  const [isIndexOpen, setIsIndexOpen] = useState(false)
  const [pendingSectionId, setPendingSectionId] = useState('')

  const visibleSections = useMemo(() => filterSections(SECTIONS, query), [query])
  const isSearching = query.trim().length > 0

  /** Reveal a section before scrolling to it: an active search may be hiding it. */
  const navigateToSection = useCallback(
    (id: string) => {
      if (!visibleSections.some((section) => section.id === id)) setQuery('')
      setPendingSectionId(id)
    },
    [visibleSections],
  )

  useEffect(() => {
    if (!pendingSectionId) return
    scrollToSection(pendingSectionId)
    setPendingSectionId('')
  }, [pendingSectionId])

  // The hash listener must see the current search state without being torn down
  // and re-armed — re-running it on every keystroke would re-scroll the page.
  const navigateRef = useRef(navigateToSection)
  useEffect(() => {
    navigateRef.current = navigateToSection
  })

  useEffect(() => {
    const followAnchor = () => {
      const id = anchoredSectionId()
      if (id) navigateRef.current(id)
    }
    followAnchor()
    window.addEventListener('hashchange', followAnchor)
    return () => window.removeEventListener('hashchange', followAnchor)
  }, [])

  return (
    <div>
      <header className="mb-6">
        <div className="flex items-center gap-3">
          <BookOpen className="h-7 w-7 text-primary" />
          <h1 className="text-3xl font-bold text-gray-900">{t('docs.title')}</h1>
        </div>
        <p className="mt-2 text-muted-foreground">{t('docs.subtitle')}</p>
      </header>

      <div className="mb-6 max-w-md">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            aria-label={t('docs.searchLabel')}
            placeholder={t('docs.searchPlaceholder')}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            className="pl-9 pr-9"
          />
          {isSearching && (
            <button
              type="button"
              onClick={() => setQuery('')}
              title={t('docs.clearSearch')}
              aria-label={t('docs.clearSearch')}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        {isSearching && (
          <p className="mt-2 text-sm text-muted-foreground">
            {t('docs.results', { count: visibleSections.length })}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
        <nav
          aria-label={t('docs.indexTitle')}
          className="w-full shrink-0 rounded-lg border border-gray-200 bg-white p-4 lg:sticky lg:top-6 lg:max-h-[calc(100vh-3rem)] lg:w-64 lg:overflow-y-auto"
        >
          <p className="hidden text-xs font-semibold uppercase tracking-wide text-muted-foreground lg:block">
            {t('docs.indexTitle')}
          </p>
          {/* Below lg the index sits above the whole manual: keep it collapsed. */}
          <button
            type="button"
            onClick={() => setIsIndexOpen((isOpen) => !isOpen)}
            aria-expanded={isIndexOpen}
            className="flex w-full items-center justify-between text-xs font-semibold uppercase tracking-wide text-muted-foreground lg:hidden"
          >
            {t('docs.indexTitle')}
            <ChevronDown
              className={`h-4 w-4 transition-transform ${isIndexOpen ? 'rotate-180' : ''}`}
            />
          </button>
          <ul className={`mt-3 space-y-1 lg:block ${isIndexOpen ? '' : 'hidden'}`}>
            {visibleSections.map((section) => (
              <li key={section.id}>
                <a
                  href={`#${section.id}`}
                  onClick={(event) => {
                    event.preventDefault()
                    navigateToSection(section.id)
                  }}
                  className={`block rounded py-1 pr-2 text-sm text-gray-600 hover:bg-gray-100 hover:text-gray-900 ${
                    INDENT_BY_LEVEL[section.level] ?? 'pl-10'
                  }`}
                >
                  {section.title}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className="min-w-0 flex-1 space-y-6">
          {visibleSections.length === 0 ? (
            <div className="rounded-lg border border-gray-200 bg-white">
              <EmptyState
                icon={Search}
                title={t('docs.emptyTitle')}
                description={t('docs.emptyBody', { query: query.trim() })}
              />
            </div>
          ) : (
            <SectionNavigationProvider value={navigateToSection}>
              {visibleSections.map((section) => (
                <ManualArticle key={section.id} section={section} />
              ))}
            </SectionNavigationProvider>
          )}
        </div>
      </div>
    </div>
  )
}
